import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { SEO_BASE_URL } from "@/lib/seoConfig";

/** Paths that must not appear in search results (GSC duplicate auth URLs, thin product pages). */
const NOINDEX_PATH_PREFIXES = ["/auth", "/sign-in", "/sign-up", "/products", "/refunds"] as const;

function isNoindexPath(pathname: string): boolean {
  return NOINDEX_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

function resolveCanonical(pathname: string): string {
  if (
    pathname === "/auth" ||
    pathname.startsWith("/auth/") ||
    pathname === "/sign-in" ||
    pathname === "/sign-up"
  ) {
    return `${SEO_BASE_URL}/auth`;
  }
  if (pathname === "/") {
    return SEO_BASE_URL;
  }
  return `${SEO_BASE_URL}${pathname}`;
}

/**
 * Sole owner of `<link rel="canonical">` for public/app routes.
 * Auth aliases and query variants canonicalize to `/auth` and are noindexed.
 */
export function RouteSEO() {
  const { pathname } = useLocation();
  const canonical = resolveCanonical(pathname);
  const noIndex = isNoindexPath(pathname);

  return (
    <Helmet>
      <link rel="canonical" href={canonical} />
      <meta property="og:url" content={canonical} />
      {noIndex ? <meta name="robots" content="noindex, follow" /> : null}
    </Helmet>
  );
}

export default RouteSEO;
